// File: src/components/questionnaires/QuestionScreen.js
// -----------------------------------------------------------------------------
// Écran des questions du questionnaire (générique)
// -----------------------------------------------------------------------------
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function QuestionScreen({ userId, gender, questions, initialAnswers, onBack, onComplete, config }) {
  const { scaleLabels, showQuestionNumber, answerQuestionIdKey, createAnswer, updateAnswer, getQuestionText, scaleMaxWidth, selectedClassName, dynamicPropositions, getAnswers, verticalLayout, buttonMaxWidth } = config;
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({}); // { questionId: { answer, userAnswerId } }
  const [showTooltip, setShowTooltip] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);

  // Initialiser les réponses et se positionner sur la bonne question
  useEffect(() => {
    if (initialAnswers && initialAnswers.length > 0 && questions.length > 0) {
      // Mapper les réponses existantes par question ID
      const answersMap = {};
      initialAnswers.forEach(ua => {
        answersMap[ua[answerQuestionIdKey]] = {
          answer: ua.answer,
          userAnswerId: ua.id
        };
      });
      setAnswers(answersMap);

      // Se positionner sur la prochaine question non répondue
      const firstUnanswered = questions.findIndex(q => !answersMap[q.id]);
      if (firstUnanswered !== -1) {
        setCurrentQuestion(firstUnanswered);
      } else {
        // Toutes les questions sont répondues
        onComplete();
      }
    }
  }, [initialAnswers, questions, onComplete, answerQuestionIdKey]);

  const question = questions[currentQuestion];
  const totalQuestions = questions.length || 30;
  const answeredCount = Object.keys(answers).length;
  const timeRemaining = Math.max(1, Math.ceil((totalQuestions - answeredCount) * 10 / 60));

  const handleAnswer = async (value) => {
    if (isAnimating || !question) return;

    setIsAnimating(true);
    setSelectedValue(value);

    try {
      const existingAnswer = answers[question.id];

      if (existingAnswer) {
        // Mettre à jour la réponse existante
        await updateAnswer(existingAnswer.userAnswerId, value);
        setAnswers(prev => ({
          ...prev,
          [question.id]: { ...prev[question.id], answer: value }
        }));
      } else {
        // Créer une nouvelle réponse
        const response = await createAnswer({
          userId,
          questionId: question.id,
          answer: value
        });
        setAnswers(prev => ({
          ...prev,
          [question.id]: { answer: value, userAnswerId: response.id }
        }));
      }

      // Délai de 500ms avant de passer à la question suivante
      setTimeout(() => {
        setSelectedValue(null);
        if (currentQuestion < questions.length - 1) {
          setCurrentQuestion(prev => prev + 1);
        } else {
          onComplete();
        }
        setIsAnimating(false);
      }, 200);
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
      setSelectedValue(null);
      setIsAnimating(false);
    }
  };

  const goBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const goForward = () => {
    if (currentQuestion < 30) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const getCurrentAnswer = () => {
    if (!question) return null;
    return answers[question.id]?.answer;
  };

  if (questions.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col p-4 pb-20 bg-gray-900 min-h-screen relative">
      {/* Blobs decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-72 h-72 rounded-full blur-[80px] opacity-30 bg-indigo-500 -top-24 -right-24 animate-blob1" />
        <div className="absolute w-64 h-64 rounded-full blur-[80px] opacity-30 bg-violet-500 -bottom-12 -left-20 animate-blob2" />
        <div className="absolute w-52 h-52 rounded-full blur-[80px] opacity-25 bg-pink-500 bottom-1/5 -right-16 animate-blob3" />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex gap-2 ml-[60px] sm:ml-0">
          <button
            onClick={goBack}
            disabled={currentQuestion === 0}
            className="w-11 h-11 flex items-center justify-center bg-white/5 border border-white/10 rounded-xl text-[#a5b4c4] transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:not-disabled:bg-white/10 hover:not-disabled:text-white"
            aria-label="Retour"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>

          <button
            onClick={goForward}
            disabled={currentQuestion === answeredCount}
            className="w-11 h-11 flex items-center justify-center bg-white/5 border border-white/10 rounded-xl text-[#a5b4c4] transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:not-disabled:bg-white/10 hover:not-disabled:text-white"
            aria-label="Retour"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>

          </button>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-[15px] font-semibold text-white">
            {currentQuestion + 1}/{totalQuestions}
          </span>
          <span className="text-[13px] text-slate-500 px-2.5 py-1 bg-white/5 rounded-full">
            ~{timeRemaining} min
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-white/10 rounded-sm mb-10 overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-sm"
          initial={{ width: 0 }}
          animate={{ width: `${((currentQuestion + 1) / totalQuestions) * 100}%` }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />
      </div>

      {/* Encouragement */}
      <div className='relative w-full'>
        <AnimatePresence>
          { (currentQuestion === 10 || currentQuestion === 20) && (
            <div className="absolute left-1/2 -translate-x-1/2 top-0">
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-center mb-6 px-4 py-2 bg-indigo-500/20 text-indigo-300 rounded-full text-sm"
              >
                {currentQuestion === 10 && "Super, tu avances bien ! 💪"}
                {currentQuestion === 20 && `Plus que ${totalQuestions - 20} questions, courage ! 🎯`}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Question content */}
      <div className="flex-1 flex flex-col justify-center py-5 max-w-[400px] mx-auto w-full">
        {showQuestionNumber && (
          <div className="text-[13px] font-medium text-violet-500 uppercase tracking-wider mb-4">
            Question {currentQuestion + 1}
          </div>
        )}

        <div className="min-h-[8rem] flex items-start">
          <p className="text-[22px] font-medium leading-relaxed text-slate-100">
            {getQuestionText(question, gender)}
          </p>
        </div>
      </div>

      {/* Scale / Answers */}
      {dynamicPropositions ? (
        // Mode Jourda : réponses dynamiques en liste verticale
        <div className="mx-auto w-full" style={{ maxWidth: scaleMaxWidth || '400px' }}>
          <div className="flex flex-col gap-3">
            {getAnswers(question).map((item) => {
              const isSelected = selectedValue === item.value || (!isAnimating && getCurrentAnswer() === item.value);

              return (
                <button
                  key={item.value}
                  onClick={() => handleAnswer(item.value)}
                  disabled={isAnimating}
                  style={buttonMaxWidth ? { maxWidth: buttonMaxWidth } : undefined}
                  className={`
                    w-full px-4 py-3 rounded-xl text-left transition-all duration-200 flex items-center gap-3
                    ${isSelected
                      ? `selected ${selectedClassName}`
                      : 'bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 hover:border-white/20'
                    }
                    ${isAnimating ? 'cursor-not-allowed' : ''}
                  `}
                >
                  <span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-sm font-semibold text-slate-400">
                    {item.display}
                  </span>
                  <span className="flex-1 text-[15px]">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        // Mode Valeurs : échelle horizontale
        <div className="mx-auto w-full" style={{ maxWidth: scaleMaxWidth || '400px' }}>
          {/* Scale labels */}
          <div className="flex justify-between mb-3 text-[13px] text-slate-500">
            <span>{scaleLabels[0]?.label}</span>
            <span>{scaleLabels[scaleLabels.length - 1]?.label}</span>
          </div>

          {/* Scale buttons */}
          <div className="flex justify-between gap-1">
            {scaleLabels.map((item) => {
              const isSelected = selectedValue === item.value || (!isAnimating && getCurrentAnswer() === item.value);

              return (
                <div key={item.value} className="relative flex flex-col items-center">
                  <button
                    onClick={() => handleAnswer(item.value)}
                    onMouseEnter={() => setShowTooltip(item.value)}
                    onMouseLeave={() => setShowTooltip(null)}
                    disabled={isAnimating}
                    className={`
                      scale-button w-12 h-12 rounded-xl text-lg font-semibold transition-all duration-200
                      ${isSelected
                        ? `selected ${selectedClassName}`
                        : 'bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 hover:border-white/20'
                      }
                      ${isAnimating ? 'cursor-not-allowed' : ''}
                    `}
                  >
                    {typeof item.display === 'string' && item.display.startsWith('<svg') ? (
                      <span
                        className={`emoji-face emoji-${item.value}`}
                        dangerouslySetInnerHTML={{ __html: item.display }}
                      />
                    ) : (
                      item.display
                    )}
                  </button>

                  {/* Tooltip */}
                  <AnimatePresence>
                    {showTooltip === item.value && !isAnimating && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        className="absolute top-14 px-3 py-1.5 bg-black text-white text-xs rounded-md whitespace-nowrap z-10"
                      >
                        {item.label}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          {/* Selected label */}
          <AnimatePresence>
            {(selectedValue || getCurrentAnswer()) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center mt-4 text-indigo-400 text-sm font-medium absolute left-1/2 -translate-x-1/2"
              >
                {scaleLabels.find(s => s.value === (selectedValue || getCurrentAnswer()))?.label}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}